import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { ArrowLeft, Printer, Download, Calendar, DollarSign, User, AlertCircle, RefreshCw, FileText, CreditCard } from 'lucide-react';
import { fetchFaturaCompleta, formatCurrency, formatDate } from '../services/faturaApi';
import './FaturaPrint.css';

const DetalheFatura = ({ user }) => {
  const navigate = useNavigate();
  const { leilaoId } = useParams();
  const location = useLocation();
  
  const [faturaData, setFaturaData] = useState(location.state?.faturaData || null);
  const [leilao, setLeilao] = useState(location.state?.leilao || null);
  const [loading, setLoading] = useState(!faturaData);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user?.isLoggedIn) {
      navigate('/login');
      return;
    }

    if (!faturaData || !leilao) {
      loadFaturaData();
    }
  }, [user, leilaoId, faturaData, leilao, navigate]);

  const loadFaturaData = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log(`Carregando fatura - Leilão: ${leilaoId}, Cliente: ${user.clienteId}`);
      const data = await fetchFaturaCompleta(leilaoId, user.clienteId);
      
      if (!data.movimentos || data.movimentos.length === 0) {
        setError('Nenhuma fatura encontrada para este leilão.');
        return;
      }
      
      setFaturaData(data);
      
      // Se não temos dados do leilão, usar dados do movimento
      if (!leilao && data.movimentos.length > 0) {
        setLeilao({
          ID: leilaoId,
          LEILAO: data.movimentos[0].LEILAO,
          DATLAN: data.movimentos[0].DATLAN
        });
      }
      
    } catch (err) {
      console.error('Erro ao carregar fatura:', err);
      setError(err.message || 'Erro ao carregar detalhes da fatura');
    } finally {
      setLoading(false);
    }
  };

  const handleImprimir = () => {
    window.print();
  };

  const getTotalGeral = () => {
    if (!faturaData.comprador || faturaData.comprador.length === 0) return 0;
    return faturaData.comprador.reduce((total, item) => total + (item.VALORPAGAR || 0), 0);
  };

  const getTotalComissao = () => {
    if (!faturaData.comprador || faturaData.comprador.length === 0) return 0;
    return faturaData.comprador.reduce((total, item) => total + (item.VALORCOMISSAO || 0), 0);
  };

  if (!user?.isLoggedIn) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[rgb(25,42,86)] mx-auto"></div>
            <p className="mt-4 text-gray-600">Carregando detalhes da fatura...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-16">
          <Alert className="border-[rgb(238,29,37)] bg-red-50">
            <AlertCircle className="h-4 w-4 text-[rgb(238,29,37)]" />
            <AlertDescription className="text-[rgb(238,29,37)]">
              {error}
            </AlertDescription>
          </Alert>
          
          <div className="text-center mt-8">
            <Button 
              onClick={() => navigate('/faturas')}
              className="bg-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)]/90 font-bold"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar para Faturas
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - ocultar na impressão */}
      <div className="bg-white shadow-sm border-b-4 border-[rgb(25,42,86)] print:hidden">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <Button
              onClick={() => navigate('/faturas')}
              variant="ghost"
              className="text-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)] hover:text-white font-bold"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
            
            <Button
              onClick={handleImprimir}
              className="bg-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)]/90 font-bold"
            >
              <Printer className="h-4 w-4 mr-2" />
              Imprimir Fatura
            </Button>
          </div>
        </div>
      </div>

      {/* Conteúdo da Fatura */}
      <div className="max-w-4xl mx-auto p-8 bg-white print-container print:p-2 print:shadow-none print:max-w-full print:mx-0">
        {/* Cabeçalho da Fatura */}
        <div className="text-center mb-4 border-b-2 border-[rgb(25,42,86)] pb-3 print-header">
          <div className="flex justify-center mb-2">
            <img
              src="https://customer-assets.emergentagent.com/job_knorr-auction-proxy/artifacts/ud6gmi7h_Screenshot_7-removebg-preview.png"
              alt="Knorr Leilões"
              className="h-12 w-auto print:h-8"
            />
          </div>
          <h1 className="text-2xl font-black text-[rgb(25,42,86)] mb-1 print:text-lg print:mb-0">FATURA DE LEILÃO</h1>
          <p className="text-gray-600 text-sm print:text-xs print:mb-0">tradição, modernidade e eficiência</p>
        </div>

        {/* Informações do Leilão e Cliente */}
        <div className="grid md:grid-cols-2 gap-4 mb-4 print-section print-grid print-no-break">
          <div className="border border-gray-200 rounded p-3 print-card">
            <div className="print-card-header">
              <h3 className="text-[rgb(25,42,86)] font-bold mb-2 flex items-center print:text-sm print:mb-1">
                <Calendar className="h-4 w-4 mr-1 print:h-3 print:w-3" />
                Dados do Leilão
              </h3>
            </div>
            <div className="space-y-1 text-sm print:text-xs print-compact">
              <div><span className="font-semibold">Leilão:</span> {leilao?.LEILAO}</div>
              <div><span className="font-semibold">Data:</span> {formatDate(leilao?.DATLAN)}</div>
              <div><span className="font-semibold">Nota:</span> {faturaData.movimentos?.[0]?.CODNOT}</div>
            </div>
          </div>

          <div className="border border-gray-200 rounded p-3 print:p-2">
            <h3 className="text-[rgb(25,42,86)] font-bold mb-2 flex items-center print:text-sm print:mb-1">
              <User className="h-4 w-4 mr-1 print:h-3 print:w-3" />
              Dados do Cliente
            </h3>
            <div className="space-y-1 text-sm print:text-xs print-compact">
              <div><span className="font-semibold">Nome:</span> {faturaData.movimentos?.[0]?.NOME_CLIENTE}</div>
              <div><span className="font-semibold">Cliente ID:</span> #{user.clienteId}</div>
            </div>
          </div>
        </div>

        {/* Lotes Comprados */}
        {faturaData.lotes && faturaData.lotes.length > 0 && (
          <div className="border border-gray-200 rounded mb-3 print-section">
            <div className="bg-gray-50 p-2 border-b print:bg-white">
              <h3 className="text-[rgb(25,42,86)] font-bold flex items-center text-sm print:text-xs">
                <FileText className="h-4 w-4 mr-1 print:h-3 print:w-3" />
                Lotes Comprados
              </h3>
            </div>
            <div className="p-2">
              <table className="w-full text-xs print-table">
                <thead>
                  <tr className="border-b border-[rgb(25,42,86)]">
                    <th className="text-left py-1 px-1">Lote</th>
                    <th className="text-left py-1 px-1">Descrição</th>
                    <th className="text-right py-1 px-1">Qtd</th>
                    <th className="text-right py-1 px-1">Valor Unit.</th>
                    <th className="text-right py-1 px-1">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {faturaData.lotes.map((lote, index) => (
                    <tr key={index} className="border-b border-gray-200">
                      <td className="py-1 px-1 font-semibold">{lote.LOTEXX}</td>
                      <td className="py-1 px-1">{lote.DESLOT}</td>
                      <td className="py-1 px-1 text-right">{lote.QTDXXX}</td>
                      <td className="py-1 px-1 text-right">{formatCurrency(lote.VLRPAR)}</td>
                      <td className="py-1 px-1 text-right font-semibold">{formatCurrency(lote.VLRTOT)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Resumo Financeiro */}
        <div className="border border-gray-200 rounded mb-3 print-section">
          <div className="bg-gray-50 p-2 border-b print:bg-white">
            <h3 className="text-[rgb(25,42,86)] font-bold flex items-center text-sm print:text-xs">
              <DollarSign className="h-4 w-4 mr-1 print:h-3 print:w-3" />
              Resumo Financeiro
            </h3>
          </div>
          <div className="p-2">
            <div className="space-y-1">
              <div className="flex justify-between items-center py-1 border-b text-sm print:text-xs">
                <span>Subtotal dos Lotes:</span>
                <span className="font-semibold">{formatCurrency(getTotalGeral())}</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b text-sm print:text-xs">
                <span>Comissão:</span>
                <span className="font-semibold">{formatCurrency(getTotalComissao())}</span>
              </div>
              <div className="flex justify-between items-center py-1 border-t-2 border-[rgb(25,42,86)] text-base print:text-sm font-bold">
                <span className="font-black">TOTAL GERAL:</span>
                <span className="font-black text-[rgb(238,29,37)]">{formatCurrency(getTotalGeral())}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-3">
          {/* Formas de Pagamento */}
          {faturaData.comprador && faturaData.comprador.length > 0 && (
            <div className="border border-gray-200 rounded print-section">
              <div className="bg-gray-50 p-2 border-b print:bg-white">
                <h3 className="text-[rgb(25,42,86)] font-bold flex items-center text-sm print:text-xs">
                  <CreditCard className="h-4 w-4 mr-1 print:h-3 print:w-3" />
                  Forma de Pagamento
                </h3>
              </div>
              <div className="p-2">
                <p className="text-gray-700 font-semibold text-sm print:text-xs">
                  {faturaData.comprador[0]?.FORMA_PAGAMENTO}
                </p>
              </div>
            </div>
          )}

          {/* Parcelamento */}
          {faturaData.parcelas && faturaData.parcelas.length > 0 && (
            <div className="border border-gray-200 rounded print-section">
              <div className="bg-gray-50 p-2 border-b print:bg-white">
                <h3 className="text-[rgb(25,42,86)] font-bold flex items-center text-sm print:text-xs">
                  <FileText className="h-4 w-4 mr-1 print:h-3 print:w-3" />
                  Parcelamento ({faturaData.parcelas.length} parcelas)
                </h3>
              </div>
              <div className="p-2">
                <table className="w-full text-xs print-table">
                  <thead>
                    <tr className="border-b border-[rgb(25,42,86)]">
                      <th className="text-left py-1 px-1">Parcela</th>
                      <th className="text-left py-1 px-1">Vencimento</th>
                      <th className="text-right py-1 px-1">Valor</th>
                      <th className="text-center py-1 px-1">1ª</th>
                    </tr>
                  </thead>
                  <tbody>
                    {faturaData.parcelas.slice(0, 12).map((parcela, index) => (
                      <tr key={index} className="border-b border-gray-200">
                        <td className="py-1 px-1 text-xs">{parcela.ORDXXX}</td>
                        <td className="py-1 px-1 text-xs">{formatDate(parcela.DATVEN)}</td>
                        <td className="py-1 px-1 text-right font-semibold text-xs">{formatCurrency(parcela.VLRPAR)}</td>
                        <td className="py-1 px-1 text-center text-xs">
                          {parcela.PRIPAR === 'S' ? '✓' : ''}
                        </td>
                      </tr>
                    ))}
                    {faturaData.parcelas.length > 12 && (
                      <tr>
                        <td colSpan="4" className="py-1 px-1 text-center text-xs text-gray-500">
                          ... e mais {faturaData.parcelas.length - 12} parcelas
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Rodapé */}
        <div className="mt-4 pt-3 border-t-2 border-[rgb(25,42,86)] text-center text-gray-600 print:mt-2 print:pt-2">
          <p className="text-xs print:text-xs">
            Este documento foi gerado automaticamente pelo sistema Knorr Leilões
          </p>
          <p className="text-xs mt-1 print:text-xs print:mt-0">
            Data de emissão: {formatDate(new Date().toISOString())}
          </p>
        </div>
      </div>
    </div>
  );
};

export default DetalheFatura;